// Physics collider visualization. When debug mode is on, overlays
// every Rapier collider in the world as a wireframe outline so you can
// see what the simulation actually thinks the geometry is — handy for
// confirming the cat capsules are tracking bones, the shelf collider
// matches the visible boards, etc.
//
// Drives off `world.debugRender()` which returns line-segment vertex
// buffers + per-vertex colors. Rapier's color scheme is information-
// rich (kinematic vs dynamic vs fixed bodies use different hues) so
// we pass `vertexColors` through verbatim. Material is transparent
// with `depthTest` left on so lines properly occlude behind solid
// geometry — keeps the overlay readable without ghosting through the
// shelf.

import * as THREE from "three";
import type { World as SceneWorld } from "./scene";
import type { PhysicsHandle } from "./physics";

export interface PhysicsDebugHandle {
  setVisible: (visible: boolean) => void;
}

export function createPhysicsDebug(
  scene: SceneWorld,
  physics: PhysicsHandle,
): PhysicsDebugHandle {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(0), 3),
  );
  geometry.setAttribute(
    "color",
    new THREE.BufferAttribute(new Float32Array(0), 4),
  );

  const material = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    // Rapier's per-vertex alpha is already moderate; multiply with this
    // to dial the overall presence down so the overlay reads as a hint
    // rather than a wall of color.
    opacity: 0.65,
    depthWrite: false,
  });

  const lines = new THREE.LineSegments(geometry, material);
  lines.visible = false;
  // Rapier returns world-space vertex positions, so the LineSegments
  // mesh sits at the origin with no transform of its own. Disable
  // frustum culling — the bounding sphere we'd compute from the buffer
  // would be stale by the time it's needed since vertices change every
  // frame.
  lines.frustumCulled = false;
  scene.scene.add(lines);

  function refresh(): void {
    const { vertices, colors } = physics.world.debugRender();
    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 4));
  }

  scene.onUpdate(() => {
    if (lines.visible) refresh();
  });

  return {
    setVisible(visible) {
      lines.visible = visible;
      // Populate immediately on toggle-on so the first frame doesn't
      // render with whatever stale buffer was lingering.
      if (visible) refresh();
    },
  };
}
